import "intersection-observer";
import "@tajawal/ct-web-account-sdk/lib/utils/event-target-polyfill"; // event-target polyfill for ios and safari
import { useEffect } from "react";
import { $gtm } from "@tajawal/web-core-sdk/lib/services/gtm.service";
import dynamic from "next/dynamic";

// styles
import { type EmotionCache } from "@emotion/react";

// utils
import {
  setupHttpHeaders,
  setupStaticHttpHeaders,
} from "@tajawal/web-flights-sdk/lib/utils/http.utils";
import { useRouter } from "next/router";
import { getPartnerReferer } from "@/utils/cache.utils";
import _get from "lodash/get";

// constants
import { LANGUAGE } from "@tajawal/web-core-sdk-v2/lib/constants";

// types
import type { AppProviderContainerProps } from "@/contexts/AppContext";
import type { AppProps } from "next/app";
import type { AppMiddlewareValues } from "@/types/middleware.types";

// component
import * as React from "react";

// global style
import "react-day-picker-v8/dist/style.css";
import { getXAppName } from "@/utils/helpers.utils";

interface MyAppProps extends AppProps<AppMiddlewareValues> {
  emotionCache?: EmotionCache;
  pageProps: AppMiddlewareValues;
}

const Head = dynamic(() => import("next/head"));

const AppProvider = dynamic<AppProviderContainerProps>(() =>
  import("@/contexts/AppContext").then((mod) => mod.AppProvider),
);

function MyApp(myAppProps: MyAppProps): JSX.Element {
  const title = _get(
    myAppProps,
    "pageProps.store.appStore.config.data.homeTitle",
    {},
  );
  // Client-side cache, shared for the whole session of the user in the browser.
  const { Component, emotionCache, pageProps: props } = myAppProps;
  const language = props.language?.language ?? LANGUAGE.AR;
  const router = useRouter();
  const partnerReferrer =
    getPartnerReferer(router.query) || props.partnerReferer;
  const path = router.asPath?.split("?")[0];
  const isMobile = _get(props, "device.isMobile", false);
  const splitId = _get(props, "splitId", null);
  setupStaticHttpHeaders(isMobile);
  setupHttpHeaders(
    language,
    props.userToken,
    partnerReferrer,
    getXAppName(isMobile),
    splitId,
  );

  useEffect(() => {
    $gtm.pushDataLayer({
      event: "content-view",
      "content-name": path,
    });
  }, [path]);

  return (
    <>
      <Head>
        <title>{title?.[language] ?? ""}</title>
      </Head>
      <AppProvider
        defaultEmotionCache={emotionCache}
        pageProps={props}
        component={Component}
        partnerReferrer={partnerReferrer}
      />
    </>
  );
}

export default MyApp;
